import React from 'react';
import styled, { keyframes } from 'styled-components';
import theme from '../../styles/theme';
import { formatTextContent } from '../../services/funnelService';

const Container = styled.div`
  height: 100vh;
  overflow-y: scroll;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: ${theme.spacing.screenPadding};
  padding-top: calc(${theme.spacing.screenPadding} + 24px); /* Account for progress bar */
  padding-bottom: calc(${theme.spacing.screenPadding} + 120px); /* Extra bottom padding for mobile CTA access */
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-bottom: calc(${theme.spacing.screenPadding} + 80px); /* Extra space to ensure CTA is scrollable + 20px buffer */
    height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 120px); /* Ensure content is tall enough to scroll */
  position: relative;
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: calc(100vh - ${theme.spacing.screenPadding} * 2 - 24px - 80px);
  }
`;

const DescriptionText = styled.div`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.normal};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
  
  p {
    margin-bottom: ${theme.spacing.md};
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  /* Support for HTML tags */
  strong, b {
    font-weight: ${theme.typography.weights.semiBold};
    color: ${theme.colors.text};
  }
  
  em, i {
    font-style: italic;
  }
  
  /* Line breaks work naturally, no special CSS needed */
  
  @media screen and (max-width: ${theme.breakpoints.mobileLg}) {
    font-size: ${theme.typography.sizes.body};
  }
`;

const ValueBoxesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.lg};
  max-width: 1000px;
  margin: 0 auto ${theme.spacing.xl} auto;
  padding: 0 ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
    padding: 0 ${theme.spacing.sm};
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const ValueBox = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.md};
  border: 2px solid ${theme.colors.doryLighterBlue};
  text-align: center;
  animation: ${fadeInUp} 0.6s ease-out forwards;
  animation-delay: ${props => props.delay || 0}ms;
  opacity: 0;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
    border-color: ${theme.colors.doryBlue};
  }
  
  /* Add a subtle gradient overlay */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(64, 92, 115, 0.02) 0%, rgba(64, 92, 115, 0.05) 100%);
    pointer-events: none;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: ${theme.spacing.lg};
  }
`;

const ValueIcon = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 5px auto;
  position: relative;
  z-index: 1;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center;
  }
  
  /* Fallback styling when no image - show emoji with circular background */
  &:not(:has(img)) {
    border-radius: ${theme.borderRadius.full};
    background: ${props => props.color || theme.colors.doryBlue};
    font-size: 2.5rem;
    color: white;
    box-shadow: ${theme.shadows.sm};
    
    &::after {
      content: '${props => props.icon || '📦'}';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 2;
    }
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 70px;
    height: 70px;
    margin-bottom: 5px;
    
    &:not(:has(img)) {
      font-size: 2rem;
    }
  }
`;

const ValueTitle = styled.h3`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h3};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.doryBlue};
  margin: 0 0 ${theme.spacing.md} 0;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.h4};
    margin-bottom: ${theme.spacing.sm};
  }
`;

const ValueDescription = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  line-height: ${theme.typography.lineHeights.relaxed};
  margin: 0;
  position: relative;
  z-index: 1;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.sizes.small};
  }
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.buttonPadding};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.touch};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast} ${theme.animation.easings.easeOut};
  min-height: 48px; /* Ensure good touch target size */
  box-shadow: ${theme.shadows.sm};
  margin-top: 0;
  margin-bottom: 24px;
  min-width: 200px;
  width: fit-content;
  max-width: 90%;
  align-self: center;
  white-space: normal;
  line-height: 1.2;
  text-align: center;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  position: relative;
  z-index: 10;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:active {
    transform: translateY(0);
    box-shadow: ${theme.shadows.sm};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  /* Mobile-specific improvements */
  @media (max-width: ${theme.breakpoints.tablet}) {
    min-height: 56px; /* Larger touch target on mobile */
    padding: 1rem 1.5rem; /* More padding for better touch experience */
  }
  
  /* For very small screens, ensure text fits */
  @media (max-width: 320px) {
    font-size: 0.875rem;
    padding: 0.75rem 1rem 2.0rem 1rem;
    min-width: 150px;
  }
`;

const MembershipDetailsScreen = ({ data, onNext, userProgress = {} }) => {
  const handleContinue = () => {
    onNext?.(data.key);
  };

  // Get formatted HTML content for the description text
  const htmlContent = formatTextContent(data.content?.text, userProgress);

  // Default value boxes if none provided in content
  const defaultValueBoxes = [
    {
      icon: '🛠️',
      title: 'Tools',
      description: 'Easily and precisely track and improve your Optimal Pace',
      color: theme.colors.doryBlue
    },
    {
      icon: '📚',
      title: 'Guidance',
      description: 'Expert resources and techniques for unlocking new levels of performance',
      color: theme.colors.doryGreen
    },
    {
      icon: '🤝',
      title: 'Accountability',
      description: 'Two optional weekly calls for fast learning and potent inspiration',
      color: theme.colors.accent
    }
  ];

  // Use content value boxes if provided, otherwise use defaults
  const valueBoxes = data.content?.valueBoxes || defaultValueBoxes;

  return (
    <Container>
      <Content>
        {/* Description text */}
        {htmlContent && (
          <DescriptionText dangerouslySetInnerHTML={{ __html: htmlContent }} />
        )}
        
        {/* Value boxes grid */}
        <ValueBoxesGrid>
          {valueBoxes.map((box, index) => (
            <ValueBox key={index} delay={index * 200}>
              <ValueIcon color={box.color} icon={box.icon}>
                {box.image && (
                  <img 
                    src={box.image} 
                    alt={box.title || 'Value Box Icon'} 
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
              </ValueIcon>
              <ValueTitle>{box.title}</ValueTitle>
              <ValueDescription>{box.description}</ValueDescription>
            </ValueBox>
          ))}
        </ValueBoxesGrid>
        
        {/* CTA button */}
        <ContinueButton onClick={handleContinue}>
          {data.content?.ctaText || 'Continue'}
        </ContinueButton>
      </Content>
    </Container>
  );
};

export default MembershipDetailsScreen;
